/**
 * 业务埋点统一封装
 *
 * 设计原则：
 *  - 优先 navigator.sendBeacon（页面卸载也不丢，主线程零阻塞）
 *  - 降级 fetch + keepalive: true
 *  - 任何失败都静默吞掉：埋点不能影响业务流程
 *  - 自动带 visitor_id（与 store/useApp 同源 localStorage key: zhcz_uid）
 *  - 自动带 ts / path / referrer
 *  - 1 秒去重：避免 StrictMode 双 mount / 重复点击双发
 *
 * 后端：POST /api/h5/v1/zhcz/event  { visitor_id, event_type, payload }
 *
 * 约定的 event_type（保持稳定，避免命名漂移）：
 *  - page_view          首页 / 文章 / 报告 / 分享图 路由变化
 *  - article_open       文章详情页打开（含 slug, category, stage）
 *  - drill_click        引导动作点击（含 target: open_wizard / open_school / open_share / ...）
 *  - intent_select      向导第二步确认意向学校（含 score, intent, year；校准偏好矩阵的核心信号）
 *  - oa_card_view       公众号 CTA 卡片露出（IntersectionObserver）
 *  - feedback_submit    回填录取结果提交成功
 */

const ENDPOINT = '/api/h5/v1/zhcz/event'
const STORAGE_KEY_UID = 'zhcz_uid'

function getVisitorId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_UID)
  } catch {
    return null
  }
}

// 1 秒内同一 event+payload 去重（生产环境也保留：防止用户连点）
const dedupeMap = new Map<string, number>()
const DEDUPE_WINDOW_MS = 1000

function shouldDedupe(key: string): boolean {
  const now = Date.now()
  const last = dedupeMap.get(key)
  if (last && now - last < DEDUPE_WINDOW_MS) return true
  dedupeMap.set(key, now)
  // 简易清理：超过 200 条时砍掉一半
  if (dedupeMap.size > 200) {
    const entries = Array.from(dedupeMap.entries()).sort((a, b) => a[1] - b[1])
    for (let i = 0; i < entries.length / 2; i++) {
      dedupeMap.delete(entries[i][0])
    }
  }
  return false
}

export interface TrackPayload {
  [key: string]: unknown
}

export function track(event_type: string, payload?: TrackPayload): void {
  if (typeof window === 'undefined') return

  try {
    // 自动补充上下文
    const enriched: TrackPayload = {
      ...payload,
      _path: window.location.pathname + window.location.search,
      _ref: document.referrer || undefined,
      _ts: Date.now()
    }

    const dedupeKey = event_type + '|' + JSON.stringify({
      // 只用关键标识做 dedupe key，避免时间戳干扰
      ...payload,
      _path: enriched._path
    })
    if (shouldDedupe(dedupeKey)) return

    const body = JSON.stringify({
      visitor_id: getVisitorId() ?? undefined,
      event_type,
      payload: enriched
    })

    // 优先 sendBeacon（页面切换/关闭时也能发出去）
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([body], { type: 'application/json' })
        const ok = navigator.sendBeacon(ENDPOINT, blob)
        if (ok) return
      } catch {
        /* fallthrough to fetch */
      }
    }

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {
      /* 静默 */
    })
  } catch {
    /* 埋点永不抛错 */
  }
}

/**
 * 路由变化时上报 page_view（在 main.tsx 内挂载一次即可）
 * 使用 history pushState/replaceState + popstate 监听，覆盖 react-router 的所有导航
 */
export function installPageViewTracker(): void {
  if (typeof window === 'undefined') return
  if ((window as any).__zhcz_pv_installed) return
  ;(window as any).__zhcz_pv_installed = true

  const fire = () => {
    track('page_view', {
      path: window.location.pathname,
      search: window.location.search || undefined
    })
  }

  // 首次 mount
  fire()

  // 拦截 history.pushState / replaceState（react-router 走 history API）
  const wrap = (key: 'pushState' | 'replaceState') => {
    const original = history[key]
    history[key] = function (this: History, ...args: any[]) {
      const ret = (original as any).apply(this, args)
      // 微任务延后，让 react-router 先更新 location
      queueMicrotask(fire)
      return ret
    } as any
  }
  wrap('pushState')
  wrap('replaceState')

  window.addEventListener('popstate', fire)
}
